/**
 * product-gallery.js
 * -----------------------------------------------------------------------------
 * 1. Disables the PhotoSwipe / magnifier lightbox on the product page.
 * 2. Makes the thumbnail strip clickable (thumb click -> main slide changes).
 * 3. Keeps the overlay prev/next arrows in sync with the active slide.
 *
 * Loaded after product-page.js so it can override btyPhotoswipeHandle.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------
   * 1. Kill the lightbox / zoom behaviour
   * ------------------------------------------------------------------- */
  if (typeof window.btyPhotoswipeHandle === 'function') {
    window.btyPhotoswipeHandle = function () {};
  }

  function unbindLightbox(scope) {
    (scope || document).querySelectorAll('.product-main-slide').forEach(function (el) {
      el.onclick = null;
      el.removeAttribute('data-pswp-uid');
    });
  }

  /* ---------------------------------------------------------------------
   * 2. Resolve the underlying slider API on <mivo-slider>
   * ------------------------------------------------------------------- */
  function isSliderApi(obj) {
    return !!obj && typeof obj.scrollTo === 'function' && typeof obj.selectedScrollSnap === 'function';
  }

  function getSliderApi(el) {
    var candidates = [el.slider, el.embla, el.emblaApi, el.api, el.mainSlider, el._slider];

    for (var i = 0; i < candidates.length; i++) {
      if (isSliderApi(candidates[i])) return candidates[i];
    }

    for (var key in el) {
      if (isSliderApi(el[key])) return el[key];
    }

    return null;
  }

  /* ---------------------------------------------------------------------
   * 3. Gallery init
   * ------------------------------------------------------------------- */
  function initGallery(gallery) {
    if (gallery.dataset.galleryReady === 'true') return;

    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('.mivo-thumbs-slide'));
    var slides = Array.prototype.slice.call(gallery.querySelectorAll('.mivo-slider-container > .mivo-slide'));
    var prevBtn = gallery.querySelector('.product-media-nav .mivo-button-prev');
    var nextBtn = gallery.querySelector('.product-media-nav .mivo-button-next');

    if (slides.length < 2) {
      gallery.dataset.galleryReady = 'true';
      return;
    }

    var api = getSliderApi(gallery);
    var current = 0;
    var syncing = false;

    function setSelected(index) {
      current = index;

      thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('is-selected', i === index);
        var btn = thumb.querySelector('button');
        if (btn) btn.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      if (prevBtn) prevBtn.disabled = index === 0;
      if (nextBtn) nextBtn.disabled = index === slides.length - 1;

      // Keep the active thumb visible inside a scrollable strip.
      var active = thumbs[index];
      var viewport = gallery.querySelector('.mivo-thumbs-slider-viewport');
      if (active && viewport && viewport.scrollWidth > viewport.clientWidth) {
        var left = active.offsetLeft - viewport.clientWidth / 2 + active.clientWidth / 2;
        viewport.scrollTo({ left: left, behavior: 'smooth' });
      }
    }

    function goTo(index) {
      index = Math.max(0, Math.min(slides.length - 1, index));
      if (index === current) return;

      if (api) {
        api.scrollTo(index);
        // 'select' listener below will call setSelected.
        if (typeof api.on !== 'function') setSelected(index);
        return;
      }

      // Fallback: drive the wired arrow buttons one step at a time.
      var delta = index - current;
      var btn = delta > 0 ? nextBtn : prevBtn;
      if (!btn) return;

      syncing = true;
      for (var i = 0; i < Math.abs(delta); i++) {
        btn.disabled = false;
        btn.click();
      }
      syncing = false;

      setSelected(index);
    }

    if (api && typeof api.on === 'function') {
      api.on('select', function () {
        setSelected(api.selectedScrollSnap());
      });
    }

    thumbs.forEach(function (thumb, index) {
      thumb.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        goTo(index);
      });
    });

    // IMPORTANT: <mivo-slider> already navigates on arrow click via the
    // _prevBtn / _nextBtn selectors in data-options. We must NOT navigate here
    // too, or every click advances twice (1 -> 3). When an API is present the
    // 'select' listener above keeps us in sync. Without an API we only mirror
    // the index so the thumbs and disabled states stay correct.
    if (!api) {
      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          if (!syncing && current > 0) setSelected(current - 1);
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          if (!syncing && current < slides.length - 1) setSelected(current + 1);
        });
      }
    }

    setSelected(0);
    gallery.dataset.galleryReady = 'true';
  }

  function init(scope) {
    var root = scope || document;
    unbindLightbox(root);

    root.querySelectorAll('[data-product-gallery]').forEach(function (gallery) {
      // <mivo-slider> upgrades asynchronously; wait a tick for its API.
      requestAnimationFrame(function () {
        initGallery(gallery);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    init();
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:select', function (event) {
    unbindLightbox(event.target);
  });
})();
